package app.footyguide.web.theme

import app.footyguide.core.theme.ClubCatalog
import app.footyguide.core.theme.ClubColorEngine
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLElement

/**
 * FootyTheme — Precise 3-color identities for every club.
 * This version maintains a consistent "Night Stadium" (Black/Dark) background
 * and uses club colors only for details, icons, and highlights.
 */
class FootyTheme(
    val primary: String,   // Main club color (highlights, active states)
    val secondary: String, // Secondary club color (subtle details)
    val tertiary: String,  // Tertiary club color (extra highlights)
    val bg: String = "#FAFAF7",      // Stitch Newsprint Paper Canvas
    val surface: String = "#FFFFFF", // Stitch Pure White Card Sheet
    val isDark: Boolean = false      // Broad-sheet paper substrate by default
) {
    fun apply() {
        val root = document.documentElement as? HTMLElement ?: return
        val derived = ClubColorEngine.derive(primary, secondary)

        root.style.setProperty("--bg", bg)
        root.style.setProperty("--surface", surface)
        root.style.setProperty("--accent", derived.fill)
        root.style.setProperty("--accent-fill", derived.fill)
        root.style.setProperty("--accent-fill-rgb", hexToRgb(derived.fill))
        root.style.setProperty("--accent-on-fill", derived.onFill)
        root.style.setProperty("--accent-secondary", secondary)
        root.style.setProperty("--accent-tertiary", tertiary)
        
        // Ink colors always calibrated for maximum readability on Stitch Paper
        root.style.setProperty("--fg", "#16181D")
        root.style.setProperty("--muted", "#71717A")
        root.style.setProperty("--border", "#E7E7E1")
        
        // Subtle banner tint on paper
        root.style.setProperty("--banner-bg", "rgba(" + hexToRgb(derived.fill) + ", 0.08)")
        
        root.setAttribute("data-theme", "paper")
        document.querySelector("meta[name=\"theme-color\"]")?.setAttribute("content", bg)
    }

    companion object {
        val Default = FootyTheme(
            primary = "#0B6E4F", // Pitch Green oficial do Stitch
            secondary = "#16181D",
            tertiary = "#F4B728",
            bg = "#FAFAF7",
            surface = "#FFFFFF",
            isDark = false
        )

        fun fromTeamColors(
            primary: String?, 
            secondary: String?, 
            tertiary: String?,
            isDark: Boolean = false
        ): FootyTheme {
            val p = primary?.takeIf { it.startsWith("#") && it.uppercase() != "#FFFFFF" }
                ?: secondary?.takeIf { it.startsWith("#") && it.uppercase() != "#FFFFFF" }
                ?: "#16181D"
            val s = secondary?.takeIf { it.startsWith("#") } ?: "#16181D"
            val t = tertiary?.takeIf { it.startsWith("#") } ?: "#F4B728"

            return FootyTheme(
                primary = p,
                secondary = s,
                tertiary = t,
                bg = "#FAFAF7",
                surface = "#FFFFFF",
                isDark = isDark
            )
        }

        private fun hexToRgb(hex: String): String {
            return try {
                val h = hex.removePrefix("#")
                val r = h.substring(0, 2).toInt(16)
                val g = h.substring(2, 4).toInt(16)
                val b = h.substring(4, 6).toInt(16)
                "$r, $g, $b"
            } catch (e: Exception) {
                "34, 197, 94"
            }
        }

        fun applyStored() {
            val storedSlug = try {
                window.localStorage.getItem("fg-my-team")
            } catch (e: Exception) {
                null
            }
            val club = storedSlug?.let { ClubCatalog.bySlug(it) }
            if (club != null) {
                fromTeamColors(club.c1, club.c2, club.c3).apply()
            } else {
                Default.apply()
            }
        }
    }
}
